/**
 * Stops browser swipe-back / forward-from-edge navigation triggered by horizontal
 * two-finger trackpad scroll on the canvas wrapper.
 *
 * React Flow calls `stopImmediatePropagation()` on wheel events it handles (panning), so
 * those never reach this listener. It only runs for events RF skips: `nowheel` regions
 * (section titles, text editors, note titles) and overlays — the cases that would
 * otherwise hit the browser navigation layer.
 *
 * React's synthetic `onWheel` is passive and cannot `preventDefault()`, so a native
 * non-passive listener is required. Horizontally-dominant deltas call `preventDefault()`;
 * vertical-dominant scroll is left alone so editor overflow scroll still works when the
 * trackpad has slight horizontal drift. Ctrl+wheel (pinch) is ignored for zoom.
 */

import { useEffect } from "react";
import { useCanvasUi } from "../context/CanvasUiContext";

export function useCanvasPreventSwipeBackNavigation() {
  const { reactFlowWrapperRef } = useCanvasUi();

  useEffect(() => {
    const el = reactFlowWrapperRef.current;
    if (!el) return;

    const handler = (e: WheelEvent) => {
      if (e.ctrlKey) return;
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) e.preventDefault();
    };

    el.addEventListener("wheel", handler, { passive: false });
    return () => el.removeEventListener("wheel", handler);
  }, [reactFlowWrapperRef]);
}
